import React from 'react';

import { Box } from '@mui/material';

const PortalContent = React.memo(
	({
		id,
		componentName,
		...props
	}: {
		id: string;
		componentName: string;
	} & unknown) => {
		const handleDismiss = React.useCallback(() => globalThis.portalRef?.unmount(id), [id]);
		if (!(globalThis.portalRef && componentName in globalThis.portalRef.components)) {
			throw new Error(`trying to render unknown portal ${componentName}`);
		}
		const Component = globalThis.portalRef.components[componentName];
		return (
			<Box position="absolute" top="0" bottom="0" left="0" right="0">
				<Component {...props} dismissPortal={handleDismiss} />
			</Box>
		);
	}
);

const PortalProvider = ({ children }: { children: JSX.Element }): JSX.Element => {
	const [contents, setContents] = React.useState<
		{ id: string; componentName: string; props: unknown }[]
	>([]);

	// register the global portal ref, using globalThis ensures fast refresh works
	React.useLayoutEffect(() => {
		globalThis.portalRef = {
			components: {},
			render: (id: string, componentName: string, props: unknown): void => {
				setContents((cs) => {
					const contentIndex = cs.findIndex((c) => c.id === id);
					if (contentIndex >= 0) {
						const newContents = [...cs];
						newContents[contentIndex] = { id, componentName, props };
						return newContents;
					} else {
						return [...cs, { id, componentName, props }];
					}
				});
			},
			unmount: (id: string): void => {
				setContents((cs) => cs.filter((c) => c.id !== id));
			},
		};
		// reset global portal ref on unmount
		return () => {
			globalThis.portalRef = null;
		};
	}, []);

	return (
		<Box width="100%" minHeight="100vh" position="relative">
			{children}
			{contents.map(({ id, componentName, props }) => (
				<PortalContent key={id} id={id} componentName={componentName} {...props} />
			))}
		</Box>
	);
};

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
PortalProvider.render = function <P>(
	id: string,
	Component: React.ComponentType<P>,
	props: { [key: string]: unknown }
): void {
	const componentName = Component.name;
	if (!componentName) throw new Error('Portal components need a name attribute');
	if (!globalThis.portalRef) throw new Error('No PortalProvider present');
	// register component if it doesn't exist yet
	if (!(componentName in globalThis.portalRef.components)) {
		globalThis.portalRef.components[componentName] = Component;
	}
	globalThis.portalRef.render(id, componentName, props);
};

PortalProvider.unmount = (id: string): void => globalThis.portalRef?.unmount(id);

export default PortalProvider;
